import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { db } from '../../lib/firebase.js';
import { collection, getDocs } from 'firebase/firestore';

const COLORS = ['#007FFF', '#4CAF50', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#FF5722', '#795548'];

function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [analytics, setAnalytics] = useState({
    storiesOverTime: [],
    usersOverTime: [],
    contentByTheme: [],
    contentBySetting: [],
    audioUsage: [],
    imageStats: [],
    hourlyActivity: [],
    weeklyPattern: [],
    topUsers: [],
  });

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();

    switch (timeRange) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 30);
    }

    return { start, end };
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();

      const [storiesSnapshot, charactersSnapshot] = await Promise.all([
        getDocs(collection(db, 'stories')),
        getDocs(collection(db, 'characters')),
      ]);

      const allStories = storiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const allCharacters = charactersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter by date range
      const stories = allStories.filter(s => {
        const date = new Date(s.created_at);
        return date >= start && date <= end;
      });

      // Stories over time
      const dailyStories = {};
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const key = date.toISOString().split('T')[0];
        dailyStories[key] = 0;
      }

      stories.forEach(story => {
        const key = story.created_at.split('T')[0];
        if (dailyStories.hasOwnProperty(key)) {
          dailyStories[key]++;
        }
      });

      const storiesOverTime = Object.entries(dailyStories).map(([date, count]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        stories: count,
      }));

      // Cumulative users over time
      const userFirstSeen = {};
      [...allStories, ...allCharacters].forEach(item => {
        if (!item.user_id || !item.created_at) return;
        const date = item.created_at.split('T')[0];
        if (!userFirstSeen[item.user_id] || date < userFirstSeen[item.user_id]) {
          userFirstSeen[item.user_id] = date;
        }
      });

      const dailyNewUsers = {};
      Object.values(userFirstSeen).forEach(date => {
        if (!dailyNewUsers[date]) dailyNewUsers[date] = 0;
        dailyNewUsers[date]++;
      });

      let cumulativeUsers = 0;
      const usersOverTime = Object.entries(dailyStories).map(([date]) => {
        cumulativeUsers += dailyNewUsers[date] || 0;
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          users: cumulativeUsers,
          newUsers: dailyNewUsers[date] || 0,
        };
      });

      // Content by theme
      const themeCounts = {};
      stories.forEach(story => {
        (story.themes || []).forEach(theme => {
          themeCounts[theme] = (themeCounts[theme] || 0) + 1;
        });
      });

      const contentByTheme = Object.entries(themeCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);

      // Content by setting
      const settingCounts = {};
      stories.forEach(story => {
        const setting = story.setting || 'Not specified';
        settingCounts[setting] = (settingCounts[setting] || 0) + 1;
      });

      const contentBySetting = Object.entries(settingCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);

      // Audio usage
      const voiceCounts = { 'No Audio': 0 };
      stories.forEach(story => {
        if (story.selected_voice) {
          voiceCounts[story.selected_voice] = (voiceCounts[story.selected_voice] || 0) + 1;
        } else {
          voiceCounts['No Audio']++;
        }
      });

      const audioUsage = Object.entries(voiceCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Image generation stats
      let totalImages = 0;
      let successfulImages = 0;
      let failedImages = 0;
      let pendingImages = 0;

      stories.forEach(story => {
        (story.pages || []).forEach(page => {
          totalImages++;
          if (page.imageUrl) successfulImages++;
          else if (page.imageStatus === 'failed') failedImages++;
          else pendingImages++;
        });
      });

      const imageStats = [
        { name: 'Successful', value: successfulImages, color: '#4CAF50' },
        { name: 'Failed', value: failedImages, color: '#F44336' },
        { name: 'Pending', value: pendingImages, color: '#FF9800' },
      ];

      // Hourly activity pattern
      const hourCounts = Array(24).fill(0);
      stories.forEach(story => {
        const hour = new Date(story.created_at).getHours();
        hourCounts[hour]++;
      });

      const hourlyActivity = hourCounts.map((count, hour) => ({
        hour: `${hour.toString().padStart(2, '0')}:00`,
        stories: count,
      }));

      // Weekly pattern
      const dayCounts = Array(7).fill(0);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      stories.forEach(story => {
        const day = new Date(story.created_at).getDay();
        dayCounts[day]++;
      });

      const weeklyPattern = dayCounts.map((count, day) => ({
        day: dayNames[day],
        stories: count,
      }));

      // Top users
      const userCounts = {};
      stories.forEach(story => {
        if (!story.user_id) return;
        userCounts[story.user_id] = (userCounts[story.user_id] || 0) + 1;
      });

      const topUsers = Object.entries(userCounts)
        .map(([userId, count]) => ({ userId: userId.substring(0, 20) + '...', count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setAnalytics({
        storiesOverTime,
        usersOverTime,
        contentByTheme,
        contentBySetting,
        audioUsage,
        imageStats,
        hourlyActivity,
        weeklyPattern,
        topUsers,
        totalStories: stories.length,
        totalImages,
        successRate: totalImages > 0 ? ((successfulImages / totalImages) * 100).toFixed(1) : 0,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Analytics & Insights
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(e, value) => value && setTimeRange(value)}
          size="small"
        >
          <ToggleButton value="7d">7 Days</ToggleButton>
          <ToggleButton value="30d">30 Days</ToggleButton>
          <ToggleButton value="90d">90 Days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {analytics.totalStories}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stories in Period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {analytics.totalImages}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Images Generated
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {analytics.successRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Image Success Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
                {analytics.audioUsage.filter(a => a.name !== 'No Audio').reduce((sum, a) => sum + a.value, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Audio Stories
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Stories Generated Over Time
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={analytics.storiesOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval={timeRange === '7d' ? 0 : 'preserveStartEnd'}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="stories"
                  stroke="#007FFF"
                  fill="#007FFF"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Popular Themes
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={analytics.contentByTheme}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {analytics.contentByTheme.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value) => <span style={{ fontSize: 11 }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              User Growth
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={analytics.usersOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval={timeRange === '7d' ? 0 : 'preserveStartEnd'}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="users"
                  name="Total Users"
                  stroke="#007FFF"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="newUsers"
                  name="New Users"
                  stroke="#4CAF50"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Story Settings Distribution
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={analytics.contentBySetting} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10 }}
                  width={100}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#007FFF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 3 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Audio Voice Usage
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={analytics.audioUsage}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={false}
                >
                  {analytics.audioUsage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Activity by Day of Week
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={analytics.weeklyPattern}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="stories" fill="#4CAF50" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Image Generation Status
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={analytics.imageStats}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {analytics.imageStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Hourly Activity & Top Users */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 300 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Activity by Hour of Day
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={analytics.hourlyActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="hour"
                  tick={{ fontSize: 10 }}
                  interval={2}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="stories"
                  stroke="#9C27B0"
                  fill="#9C27B0"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 300 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Top Users (by Stories)
            </Typography>
            <TableContainer sx={{ maxHeight: 220 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell align="right">Stories</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.topUsers.map((user, index) => (
                    <TableRow key={user.userId}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                        {user.userId}
                      </TableCell>
                      <TableCell align="right">{user.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminAnalytics;
